import React, { useState, useEffect } from 'react';
import { X, Save, TestTube, AlertCircle } from 'lucide-react';
import apiService from '../../services/api';
import { 
  AccountMatchingRule, 
  CreateAccountMatchingRuleRequest,
  TestMatchingRequest,
  TestMatchingResponse
} from '../../types/api';

interface RuleFormProps {
  rule?: AccountMatchingRule;
  isOpen: boolean;
  onClose: () => void;
  onSave: (rule: AccountMatchingRule) => void;
}

export const RuleForm: React.FC<RuleFormProps> = ({
  rule,
  isOpen,
  onClose,
  onSave
}) => {
  const [formData, setFormData] = useState<CreateAccountMatchingRuleRequest>({
    pattern_type: 'exact',
    pattern_value: '',
    trade_type: 'both',
    commission_rate: 0,
    priority: 100,
    description: ''
  });
  
  const [testData, setTestData] = useState<TestMatchingRequest>({
    account_string: '',
    trade_type: 'both'
  });
  
  const [testResult, setTestResult] = useState<TestMatchingResponse | null>(null);
  const [conflicts, setConflicts] = useState<any[]>([]);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Initialize form data when rule changes
  useEffect(() => {
    if (rule) {
      setFormData({
        account_id: rule.account_id || undefined,
        pattern_type: rule.pattern_type,
        pattern_value: rule.pattern_value,
        security_pattern: rule.security_pattern,
        trade_type: rule.trade_type,
        commission_rate: parseFloat(rule.commission_rate),
        priority: rule.priority,
        description: rule.description
      });
      setTestData({
        account_string: rule.pattern_value,
        security: rule.security_pattern,
        trade_type: rule.trade_type
      });
    } else {
      setFormData({
        pattern_type: 'exact',
        pattern_value: '',
        trade_type: 'both',
        commission_rate: 0,
        priority: 100,
        description: ''
      });
      setTestData({
        account_string: '',
        trade_type: 'both'
      });
    }
    setTestResult(null);
    setConflicts([]);
    setError(null);
  }, [rule, isOpen]);

  const handleInputChange = (field: keyof CreateAccountMatchingRuleRequest, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setTestResult(null); // Clear test results when form changes
    setConflicts([]);
  };

  const handleTestRule = async () => {
    setTesting(true);
    setError(null);
    
    try {
      const response = await apiService.testAccountMatching(testData);
      setTestResult(response.data!);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to test rule');
    } finally {
      setTesting(false);
    }
  };

  const checkConflicts = async () => {
    try {
      const response = await apiService.checkMatchingConflicts(formData);
      setConflicts(response.data || []);
    } catch (err: any) {
      console.error('Failed to check conflicts:', err);
    }
  };

  useEffect(() => {
    if (formData.pattern_value && formData.pattern_type) {
      checkConflicts();
    }
  }, [formData.pattern_value, formData.pattern_type, formData.priority]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    
    try {
      let response;
      if (rule) {
        response = await apiService.updateAccountMatchingRule(rule.id, formData);
      } else {
        response = await apiService.createAccountMatchingRule(formData);
      }
      
      onSave(response.data!);
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save rule');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div 
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
          onClick={onClose}
        />

        {/* Modal panel */}
        <div className="inline-block w-full max-w-4xl p-0 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-lg">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">
              {rule ? 'Edit Account Matching Rule' : 'Create Account Matching Rule'}
            </h3>
            <button
              onClick={onClose}
              className="p-1 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mx-6 mt-4 p-4 bg-danger-50 border border-danger-200 rounded-lg flex items-center space-x-2">
              <AlertCircle className="w-5 h-5 text-danger-600" />
              <p className="text-sm text-danger-800">{error}</p>
            </div>
          )}

          {/* Content */}
          <div className="px-6 py-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Form Section */}
              <div className="space-y-6">
                <div className="space-y-4">
                  <div>
                    <label className="label">Pattern Type</label>
                    <select
                      value={formData.pattern_type}
                      onChange={(e) => handleInputChange('pattern_type', e.target.value)}
                      className="input"
                    >
                      <option value="exact">Exact Match</option>
                      <option value="contains">Contains</option>
                      <option value="starts_with">Starts With</option>
                      <option value="ends_with">Ends With</option>
                      <option value="regex">Regular Expression</option>
                    </select>
                  </div>

                  <div>
                    <label className="label">Pattern Value *</label>
                    <input
                      type="text"
                      value={formData.pattern_value}
                      onChange={(e) => handleInputChange('pattern_value', e.target.value)}
                      className="input"
                      placeholder="e.g., INTERVAL, INTERVAL BIDS, etc."
                      required
                    />
                  </div>

                  <div>
                    <label className="label">Security Pattern (Optional)</label>
                    <input
                      type="text"
                      value={formData.security_pattern || ''}
                      onChange={(e) => handleInputChange('security_pattern', e.target.value)}
                      className="input"
                      placeholder="e.g., AAPL, SPY, etc."
                    />
                  </div>

                  <div>
                    <label className="label">Trade Type</label>
                    <select
                      value={formData.trade_type}
                      onChange={(e) => handleInputChange('trade_type', e.target.value)}
                      className="input"
                    >
                      <option value="both">Both Buy & Sell</option>
                      <option value="buy">Buy Only</option>
                      <option value="sell">Sell Only</option>
                    </select>
                  </div>

                  <div>
                    <label className="label">Commission Rate (%) *</label>
                    <input
                      type="number"
                      step="0.0001"
                      value={formData.commission_rate}
                      onChange={(e) => handleInputChange('commission_rate', parseFloat(e.target.value) || 0)}
                      className="input"
                      placeholder="e.g., 0.8337, 10.0000"
                      required
                    />
                  </div>

                  <div>
                    <label className="label">Priority</label>
                    <input
                      type="number"
                      value={formData.priority}
                      onChange={(e) => handleInputChange('priority', parseInt(e.target.value) || 100)}
                      className="input"
                      placeholder="Lower numbers = higher priority"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Lower numbers have higher priority. Rules are evaluated in priority order.
                    </p>
                  </div>

                  <div>
                    <label className="label">Description (Optional)</label>
                    <textarea
                      value={formData.description || ''}
                      onChange={(e) => handleInputChange('description', e.target.value)}
                      className="input"
                      rows={3}
                      placeholder="Describe when this rule should be used..."
                    />
                  </div>
                </div>
              </div>

              {/* Testing Section */}
              <div className="space-y-6">
                <div>
                  <h4 className="text-lg font-medium text-gray-900 mb-4">Test Rule</h4>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="label">Test Account String</label>
                      <input
                        type="text"
                        value={testData.account_string}
                        onChange={(e) => setTestData(prev => ({ ...prev, account_string: e.target.value }))}
                        className="input"
                        placeholder="Enter account string to test..."
                      />
                    </div>

                    <div>
                      <label className="label">Test Security (Optional)</label>
                      <input
                        type="text"
                        value={testData.security || ''}
                        onChange={(e) => setTestData(prev => ({ ...prev, security: e.target.value }))}
                        className="input"
                        placeholder="Enter security to test..."
                      />
                    </div>

                    <div>
                      <label className="label">Test Trade Type</label>
                      <select
                        value={testData.trade_type}
                        onChange={(e) => setTestData(prev => ({ ...prev, trade_type: e.target.value as any }))}
                        className="input"
                      >
                        <option value="both">Both</option>
                        <option value="buy">Buy</option>
                        <option value="sell">Sell</option>
                      </select>
                    </div>

                    <button
                      onClick={handleTestRule}
                      disabled={!testData.account_string || testing}
                      className="btn btn-secondary flex items-center space-x-2 w-full"
                    >
                      {testing && <div className="loading-spinner" />}
                      <TestTube className="w-4 h-4" />
                      <span>Test Pattern</span>
                    </button>
                  </div>

                  {/* Test Results */}
                  {testResult && (
                    <div className="mt-4 p-4 border rounded-lg">
                      <h5 className="font-medium text-gray-900 mb-2">Test Result</h5>
                      <div className="space-y-2 text-sm">
                        <div className="flex items-center justify-between">
                          <span className="text-gray-600">Matched:</span>
                          <span className={testResult.matched ? 'text-success-600' : 'text-danger-600'}>
                            {testResult.matched ? 'Yes' : 'No'}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-gray-600">Commission Rate:</span>
                          <span className="font-medium">{testResult.commission_rate}%</span>
                        </div>
                        {testResult.matched_rule && (
                          <div className="mt-2 p-2 bg-gray-50 rounded text-xs">
                            <strong>Matched Rule:</strong> {testResult.matched_rule.description || testResult.matched_rule.pattern_value}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Conflicts Warning */}
                {conflicts.length > 0 && (
                  <div className="p-4 bg-warning-50 border border-warning-200 rounded-lg">
                    <div className="flex items-center space-x-2 mb-2">
                      <AlertCircle className="w-5 h-5 text-warning-600" />
                      <h5 className="font-medium text-warning-900">Potential Conflicts</h5>
                    </div>
                    <p className="text-sm text-warning-800 mb-2">
                      This rule might conflict with existing rules:
                    </p>
                    <ul className="text-sm text-warning-800 space-y-1">
                      {conflicts.map((conflict, index) => (
                        <li key={index} className="flex items-center justify-between">
                          <span>Priority {conflict.priority}: {conflict.pattern_value}</span>
                          <span className="text-xs">{conflict.commission_rate}%</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end space-x-3 px-6 py-4 border-t border-gray-200">
            <button
              onClick={onClose}
              disabled={saving}
              className="btn btn-secondary"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={!formData.pattern_value || !formData.commission_rate || saving}
              className="btn btn-primary flex items-center space-x-2"
            >
              {saving && <div className="loading-spinner" />}
              <Save className="w-4 h-4" />
              <span>{rule ? 'Update' : 'Create'} Rule</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};